import React, { useState, useEffect, useImperativeHandle } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { UserCircle, Check, Spinner } from '@phosphor-icons/react';
import PhoneField, { isValidPhoneNumber } from '@/components/PhoneField';

const GENDER_OPTIONS = [
  { value: '', label: 'Prefer not to specify' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

/**
 * UserProfileCard — lets a logged-in user edit their own account info
 * (full_name, phone, gender). Email and role are read-only.
 * Shared by VC Profile and Startup Profile pages.
 *
 * Pass an `accent` prop (e.g. "#10b981" for startup green, "#6366f1" for VC indigo).
 */
const UserProfileCard = ({ user, accent = '#10b981', externalActions = false, controlsRef, onDirtyChange }) => {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '', gender: '' });

  useEffect(() => {
    let alive = true;
    const fetchMe = async () => {
      try {
        const res = await api.get('/auth/me');
        if (!alive) return;
        setMe(res.data);
        setForm({
          full_name: res.data.full_name || '',
          // Drop legacy free-text phone values that aren't valid E.164 so the
          // field falls back to the +91 default for a clean re-entry.
          phone: isValidPhoneNumber(res.data.phone || '') ? res.data.phone : '',
          gender: res.data.gender || '',
        });
      } catch (e) {
        if (alive) toast.error('Failed to load your profile');
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchMe();
    return () => { alive = false; };
  }, []);

  const dirty =
    me &&
    (form.full_name !== (me.full_name || '') ||
      form.phone !== (isValidPhoneNumber(me.phone || '') ? me.phone : '') ||
      form.gender !== (me.gender || ''));

  const validate = () => {
    if (!form.full_name.trim()) {
      toast.error('Name cannot be empty');
      return false;
    }
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (saving || !validate()) return false;
    setSaving(true);
    try {
      const res = await api.patch('/auth/me', form);
      setMe(res.data);
      // Keep the cached user in localStorage in sync so the sidebar greeting updates
      try {
        const cached = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...cached, full_name: res.data.full_name }));
      } catch { /* ignore */ }
      if (!externalActions) toast.success('Profile updated');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { onDirtyChange?.(!!dirty); }, [dirty, onDirtyChange]);
  useImperativeHandle(controlsRef, () => ({
    save: handleSave,
    validate,
    discard: () => { if (me) setForm({ full_name:me.full_name || '', phone:isValidPhoneNumber(me.phone || '') ? me.phone : '', gender:me.gender || '' }); },
  }));

  if (loading) {
    return (
      <div
        data-testid="user-profile-card-loading"
        className="bg-surface border border-border rounded-[10px] p-6 mb-6 flex items-center gap-3 text-text-secondary text-sm"
      >
        <Spinner size={16} className="animate-spin" /> Loading your profile…
      </div>
    );
  }

  return (
    <div
      className="bg-surface border border-border rounded-[10px] p-6 mb-6"
      data-testid="user-profile-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <UserCircle size={18} weight="bold" style={{ color: accent }} />
        <h3 className="font-heading font-semibold text-lg text-text-primary">Your account</h3>
      </div>
      <p className="text-[12px] text-text-muted mb-5">
        This is the personal info attached to your login. Email and role can't be changed.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Full name</label>
          <input
            type="text"
            data-testid="me-full-name-input"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] text-text-primary focus:outline-none focus:border-[var(--accent-color,#10b981)]"
            placeholder="Your name"
            maxLength={120}
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Phone</label>
          <PhoneField
            data-testid="me-phone-input"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            placeholder="98xxx xxxxx"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Email</label>
          <input
            type="email"
            data-testid="me-email-readonly"
            value={me?.email || ''}
            disabled
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] text-text-muted cursor-not-allowed opacity-70"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Gender</label>
          <select
            data-testid="me-gender-select"
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] text-text-primary focus:outline-none focus:border-[var(--accent-color,#10b981)]"
          >
            {GENDER_OPTIONS.map((opt) => (
              <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {!externalActions && <div className="flex items-center justify-end gap-3 mt-5">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid="me-save-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{ backgroundColor: accent }}
        >
          {saving ? (
            <>
              <Spinner size={14} className="animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Check size={14} weight="bold" /> Save changes
            </>
          )}
        </button>
      </div>}
    </div>
  );
};

export default UserProfileCard;
